import React, { useState, useEffect, useCallback } from "react";

const FibonacciRabbits = () => {
  const [currentMonth, setCurrentMonth] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1500);
  const [showGrowthLines, setShowGrowthLines] = useState(true);
  const [showBreedingLines, setShowBreedingLines] = useState(true);
  const [filterMonth, setFilterMonth] = useState(0); // 0表示不过滤，其他数字表示要过滤的月份
  const [filterType, setFilterType] = useState("all"); // 'all', 'adult', 'young'

  // 重新设计兔子数据结构和布局算法
  const generateRabbitData = useCallback((month) => {
    const months = [];

    // 第一个月：一对小兔子
    months.push([
      { id: "r1_1", type: "young", x: 0, generation: 0, isNewBorn: false },
    ]);

    if (month >= 2) {
      // 第二个月：兔子长大
      months.push([
        { id: "r1_1", type: "adult", x: 0, generation: 0, isNewBorn: false },
      ]);
    }

    // 从第三个月开始
    for (let m = 3; m <= month; m++) {
      const prevMonth = months[m - 2];
      const newMonth = [];
      let xPosition = 0;

      // 先处理所有存在的兔子（成长）
      prevMonth.forEach((rabbit) => {
        const grownRabbit = {
          ...rabbit,
          type: rabbit.type === "young" ? "adult" : "adult",
          x: xPosition,
          isNewBorn: false,
        };
        newMonth.push(grownRabbit);

        // 如果这只兔子是成年兔子，它会生小兔子
        if (rabbit.type === "adult") {
          const newBaby = {
            id: `r${m}_${newMonth.length}`,
            type: "young",
            x: xPosition + 1, // 紧挨着父母
            generation: rabbit.generation + 1,
            parentId: rabbit.id,
            isNewBorn: true,
          };
          newMonth.push(newBaby);
          xPosition += 2; // 父母 + 孩子占2个位置
        } else {
          xPosition += 1; // 只占1个位置
        }
      });

      months.push(newMonth);
    }

    return months;
  }, []);

  const rabbitData = generateRabbitData(Math.max(currentMonth, 12));

  // 获取斐波那契数列
  const getFibonacci = (n) => {
    const fib = [1, 1];
    for (let i = 2; i < n; i++) {
      fib[i] = fib[i - 1] + fib[i - 2];
    }
    return fib;
  };

  const fibSequence = getFibonacci(currentMonth);

  // 计算SVG所需的宽度
  const calculateSVGWidth = () => {
    if (currentMonth <= 0 || !rabbitData[currentMonth - 1]) return 800;
    const maxRabbits = rabbitData
      .slice(0, currentMonth)
      .reduce((max, month) => Math.max(max, month.length), 0);
    return Math.max(800, maxRabbits * 80 + 200);
  };

  const svgWidth = calculateSVGWidth();
  const svgHeight = Math.max(600, currentMonth * 140 + 100);

  // 自动播放逻辑
  useEffect(() => {
    if (isPlaying && currentMonth < 12) {
      const timer = setTimeout(() => {
        setCurrentMonth((prev) => prev + 1);
      }, playSpeed);
      return () => clearTimeout(timer);
    } else if (isPlaying && currentMonth >= 12) {
      setIsPlaying(false);
    }
  }, [isPlaying, currentMonth, playSpeed]);

  const RabbitIcon = ({ type, x, y, id, isNewBorn }) => {
    const isYoung = type === "young";
    const size = isYoung ? 28 : 40;
    // 新生的小兔子用特殊颜色标识
    const color = isYoung ? (isNewBorn ? "#FFD700" : "#FFB6C1") : "#8B4513";
    const strokeColor = isNewBorn ? "#FF6B35" : "#333";
    const strokeWidth = isNewBorn ? "2" : "1";

    return (
      <g transform={`translate(${120 + x * 70}, ${y})`}>
        {/* 兔子身体 */}
        <ellipse
          cx={0}
          cy={0}
          rx={size / 2}
          ry={size / 3}
          fill={color}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        {/* 兔子耳朵 */}
        <ellipse
          cx={-size / 4}
          cy={-size / 3}
          rx={size / 8}
          ry={size / 4}
          fill={color}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <ellipse
          cx={size / 4}
          cy={-size / 3}
          rx={size / 8}
          ry={size / 4}
          fill={color}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        {/* 眼睛 */}
        <circle cx={-size / 6} cy={-size / 8} r={2} fill="#000" />
        <circle cx={size / 6} cy={-size / 8} r={2} fill="#000" />
        {/* 鼻子 */}
        <circle cx={0} cy={size / 8} r={1.5} fill="#FF69B4" />

        {/* ID标签 */}
        <text
          x={0}
          y={size / 2 + 15}
          textAnchor="middle"
          fontSize="9"
          fill="#333"
          fontWeight="bold"
        >
          {id.split("_")[0]}
        </text>
      </g>
    );
  };

  const ConnectionLines = ({ monthIndex, rabbits, prevRabbits }) => {
    if (!prevRabbits || monthIndex === 0) return null;

    const lines = [];
    const currentY = monthIndex * 140 + 50;
    const prevY = (monthIndex - 1) * 140 + 50;

    rabbits.forEach((rabbit) => {
      // 成长线（蓝色虚线）- 只对非新生兔子，且需要开启显示
      if (showGrowthLines && !rabbit.isNewBorn) {
        const prevRabbit = prevRabbits.find((pr) => pr.id === rabbit.id);
        if (prevRabbit) {
          lines.push(
            <line
              key={`growth-${rabbit.id}`}
              x1={120 + prevRabbit.x * 70}
              y1={prevY}
              x2={120 + rabbit.x * 70}
              y2={currentY}
              stroke="#2196F3"
              strokeWidth="2"
              strokeDasharray="5,5"
              opacity="0.6"
            />
          );
        }
      }

      // 繁殖线（红色实线）- 只对新生兔子，且需要开启显示
      if (showBreedingLines && rabbit.isNewBorn && rabbit.parentId) {
        const parent = prevRabbits.find((pr) => pr.id === rabbit.parentId);
        if (parent) {
          lines.push(
            <line
              key={`birth-${rabbit.id}`}
              x1={120 + parent.x * 70}
              y1={prevY}
              x2={120 + rabbit.x * 70}
              y2={currentY}
              stroke="#FF4444"
              strokeWidth="3"
              opacity="0.8"
            />
          );
        }
      }
    });

    return <g>{lines}</g>;
  };

  // 过滤兔子显示
  const getFilteredRabbits = (rabbits, monthIndex) => {
    // 如果没有选择过滤月份，或者当前月份不是要过滤的月份，返回所有兔子
    if (filterMonth === 0 || filterMonth !== monthIndex + 1) {
      return rabbits;
    }

    // 根据过滤类型返回对应的兔子
    switch (filterType) {
      case "adult":
        return rabbits.filter((r) => r.type === "adult");
      case "young":
        return rabbits.filter((r) => r.type === "young");
      case "all":
      default:
        return rabbits;
    }
  };

  return (
    <div className="w-full max-w-full mx-auto p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg shadow-lg">
      {/* 标题和控制面板 */}
      <div className="mb-6 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          斐波那契兔子繁殖可视化
        </h1>

        {/* 主要控制按钮 */}
        <div className="flex justify-center items-center gap-4 mb-4">
          <button
            onClick={() => setCurrentMonth(Math.max(1, currentMonth - 1))}
            disabled={currentMonth <= 1}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300 hover:bg-blue-600 transition-colors"
          >
            ← 上个月
          </button>

          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            {isPlaying ? "⏸️ 暂停" : "▶️ 播放"}
          </button>

          <button
            onClick={() => setCurrentMonth(Math.min(12, currentMonth + 1))}
            disabled={currentMonth >= 12}
            className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300 hover:bg-blue-600 transition-colors"
          >
            下个月 →
          </button>

          <select
            value={playSpeed}
            onChange={(e) => setPlaySpeed(parseInt(e.target.value))}
            className="px-3 py-2 border rounded"
          >
            <option value={3000}>慢速</option>
            <option value={1500}>正常</option>
            <option value={800}>快速</option>
          </select>

          <button
            onClick={() => setCurrentMonth(1)}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            🔄 重置
          </button>
        </div>

        {/* 连线和过滤控制 */}
        <div className="flex justify-center items-center gap-6 mb-4 flex-wrap">
          {/* 连线控制 */}
          <div className="flex items-center gap-4 p-3 bg-white rounded-lg shadow">
            <span className="font-semibold text-gray-700">连线显示：</span>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showGrowthLines}
                onChange={(e) => setShowGrowthLines(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">成长线</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showBreedingLines}
                onChange={(e) => setShowBreedingLines(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">繁殖线</span>
            </label>
          </div>

          {/* 兔子过滤控制 */}
          <div className="flex items-center gap-4 p-3 bg-white rounded-lg shadow">
            <span className="font-semibold text-gray-700">兔子过滤：</span>
            <select
              value={filterMonth}
              onChange={(e) => {
                const newFilterMonth = parseInt(e.target.value);
                setFilterMonth(newFilterMonth);
                // 当选择新的过滤月份时，重置过滤类型为显示全部
                if (newFilterMonth > 0 && filterType === "all") {
                  setFilterType("all");
                }
              }}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value={0}>不过滤</option>
              {Array.from({ length: currentMonth }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  第{i + 1}月
                </option>
              ))}
            </select>
            {filterMonth > 0 && (
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-2 py-1 border rounded text-sm"
              >
                <option value="all">显示全部</option>
                <option value="adult">只显示大兔子</option>
                <option value="young">只显示小兔子</option>
              </select>
            )}
          </div>
        </div>

        {/* 当前状态显示 */}
        <div className="flex justify-center items-center gap-6 mb-4">
          <div className="text-lg font-semibold">第 {currentMonth} 个月</div>
          <div className="text-lg">
            兔子总数:{" "}
            <span className="font-bold text-blue-600">
              {fibSequence[currentMonth - 1] || 0}
            </span>
          </div>
        </div>

        {/* 图例 */}
        <div className="flex justify-center items-center gap-6 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-6 h-4 bg-pink-300 rounded border border-gray-400"></div>
            <span className="text-sm">小兔子</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-4 bg-yellow-400 rounded border-2 border-orange-500"></div>
            <span className="text-sm">新生小兔子</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-4 bg-yellow-600 rounded border border-gray-400"></div>
            <span className="text-sm">大兔子</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-1 bg-blue-500"
              style={{
                background:
                  "repeating-linear-gradient(to right, #2196F3 0, #2196F3 5px, transparent 5px, transparent 10px)",
              }}
            ></div>
            <span className="text-sm">成长</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-red-500"></div>
            <span className="text-sm">繁殖</span>
          </div>
        </div>
      </div>

      {/* SVG 可视化区域 - 添加水平滚动 */}
      <div
        className="bg-white rounded-lg shadow-inner p-4 overflow-auto"
        style={{ maxWidth: "100%" }}
      >
        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="block mx-auto"
        >
          {/* 网格背景 */}
          <defs>
            <pattern
              id="grid"
              width="70"
              height="140"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 70 0 L 0 0 0 140"
                fill="none"
                stroke="#f0f0f0"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" opacity="0.3" />

          {/* 绘制每个月的兔子 */}
          {rabbitData.slice(0, currentMonth).map((monthRabbits, monthIndex) => {
            const filteredRabbits = getFilteredRabbits(
              monthRabbits,
              monthIndex
            );

            return (
              <g key={monthIndex}>
                {/* 月份标签和背景 */}
                <rect
                  x={10}
                  y={monthIndex * 140 + 20}
                  width={90}
                  height={60}
                  fill="#E3F2FD"
                  stroke="#2196F3"
                  strokeWidth="1"
                  rx="5"
                />
                <text
                  x={55}
                  y={monthIndex * 140 + 45}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="bold"
                  fill="#1976D2"
                >
                  第{monthIndex + 1}月
                </text>
                <text
                  x={55}
                  y={monthIndex * 140 + 65}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#666"
                >
                  {monthRabbits.length}对
                  {filterMonth === monthIndex + 1 &&
                    filterType !== "all" &&
                    ` → 显示${filteredRabbits.length}对`}
                </text>

                {/* 过滤状态提示 */}
                {filterMonth === monthIndex + 1 && filterType !== "all" && (
                  <rect
                    x={8}
                    y={monthIndex * 140 + 18}
                    width={94}
                    height={64}
                    fill="none"
                    stroke="#FF6B35"
                    strokeWidth="3"
                    strokeDasharray="5,5"
                    rx="5"
                  />
                )}

                {/* 连接线 - 基于原始数据绘制，但只有显示的兔子才会有连线 */}
                <ConnectionLines
                  monthIndex={monthIndex}
                  rabbits={monthRabbits}
                  prevRabbits={
                    monthIndex > 0 ? rabbitData[monthIndex - 1] : null
                  }
                />

                {/* 兔子 - 只绘制过滤后的兔子 */}
                {filteredRabbits.map((rabbit, rabbitIndex) => (
                  <RabbitIcon
                    key={`${monthIndex}-${rabbit.id}`}
                    type={rabbit.type}
                    x={rabbit.x}
                    y={monthIndex * 140 + 50}
                    id={rabbit.id}
                    isNewBorn={rabbit.isNewBorn}
                  />
                ))}
              </g>
            );
          })}
        </svg>
      </div>

      {/* 斐波那契数列表格显示 */}
      <div className="mt-6 p-4 bg-white rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-3">斐波那契数列:</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {fibSequence.slice(0, currentMonth).map((num, index) => (
                  <th
                    key={index}
                    className={`p-3 text-center border border-gray-300 ${
                      index === currentMonth - 1
                        ? "bg-blue-500 text-white font-bold"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    第{index + 1}月
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {fibSequence.slice(0, currentMonth).map((num, index) => (
                  <td
                    key={index}
                    className={`p-3 text-center border border-gray-300 font-semibold text-lg ${
                      index === currentMonth - 1
                        ? "bg-blue-100 text-blue-800 shadow-inner"
                        : "bg-white text-gray-800"
                    }`}
                  >
                    {num}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          <strong>规则说明：</strong>
          <br />
          • 小兔子一个月后变成大兔子
          <br />
          • 大兔子每个月生一对小兔子（金黄色显示）
          <br />
          • 蓝虚线表示兔子的成长过程，红线表示繁殖关系
          <br />•
          可以通过上方控制面板选择显示/隐藏连线，以及过滤特定月份的兔子类型
        </p>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default FibonacciRabbits;
